import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { MaskedLines } from "./Reveal";
import { HERO_VIDEO_WEBM, HERO_VIDEO_MP4, IMAGES, waLink } from "../lib/data";

const ACCENT = "#B7D77A";

const TrustChip = ({ children, className = "", style }) => (
  <motion.div
    style={style}
    className={`pointer-events-none absolute hidden lg:flex items-center gap-2 rounded-full bg-brand-forest/25 backdrop-blur-2xl border border-white/25 ring-1 ring-white/10 px-5 py-3 text-sm font-medium text-white shadow-lg shadow-black/20 ${className}`}
  >
    <span className="h-1.5 w-1.5 rounded-full" style={{ background: ACCENT }} />
    {children}
  </motion.div>
);

export const ScrollVideoHero = () => {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const targetTime = useRef(0);
  const seeking = useRef(false);
  const lastSeekAt = useRef(0);

  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const progress = useMotionValue(0);

  const contentY = useTransform(progress, [0, 1], [0, -140]);
  const contentOpacity = useTransform(progress, [0, 0.75, 1], [1, 1, 0.35]);
  const hintOpacity = useTransform(progress, [0, 0.08], [1, 0]);
  const chipLeftX = useTransform(progress, [0, 1], [0, -32]);
  const chipRightX = useTransform(progress, [0, 1], [0, 48]);
  const chipY = useTransform(progress, [0, 1], [40, -40]);

  // Detect mobile once
  useEffect(() => {
    setIsMobile(window.matchMedia("(max-width: 768px)").matches);
  }, []);

  // Video load handler (attached via React props on the <video> element)
  const handleLoaded = () => {
    const vid = videoRef.current;
    if (!vid) return;
    setReady(true);
    if (isMobile) {
      vid.play().catch(() => {});
    } else {
      try {
        vid.currentTime = 0.001;
      } catch (e) {
        /* seeking before ready — safe to ignore */
      }
    }
  };

  // Single rAF loop: derive scroll progress from layout, scrub the video,
  // and drive the parallax — independent of any scroll library.
  useEffect(() => {
    let raf;
    const tick = () => {
      const wrap = wrapRef.current;
      const vid = videoRef.current;
      if (wrap) {
        const rect = wrap.getBoundingClientRect();
        const total = rect.height - window.innerHeight;
        const p = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
        progress.set(p);

        if (!isMobile && vid) {
          const dur = vid.duration;
          if (Number.isFinite(dur) && dur > 0 && vid.readyState >= 2) {
            targetTime.current = Math.min(dur - 0.05, Math.max(0, p * dur));
            // release a stuck seek lock if onSeeked never fired
            if (seeking.current && performance.now() - lastSeekAt.current > 250) {
              seeking.current = false;
            }
            if (!seeking.current) {
              const cur = vid.currentTime;
              const diff = targetTime.current - cur;
              if (Math.abs(diff) > 0.015) {
                seeking.current = true;
                lastSeekAt.current = performance.now();
                try {
                  vid.currentTime = cur + diff * 0.25;
                } catch (e) {
                  seeking.current = false;
                }
              }
            }
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isMobile, progress]);

  return (
    <section ref={wrapRef} data-testid="scroll-video-hero" className="relative h-[260vh]">
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-brand-dark">
        {/* Video layer — kept clear, no full-screen shader */}
        <video
          ref={videoRef}
          data-testid="hero-scroll-video"
          className="absolute inset-0 h-full w-full object-cover"
          muted
          playsInline
          loop={isMobile}
          autoPlay={isMobile}
          preload="auto"
          poster={IMAGES.heroProduct}
          onLoadedData={handleLoaded}
          onCanPlay={() => setReady(true)}
          onSeeked={() => {
            seeking.current = false;
          }}
          onError={() => setFailed(true)}
        >
          <source src={HERO_VIDEO_WEBM} type="video/webm" />
          <source src={HERO_VIDEO_MP4} type="video/mp4" />
        </video>

        {/* Subtle top scrim — only to guarantee nav legibility, video stays clear */}
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/25 to-transparent pointer-events-none z-[5]" />

        {/* Loading state */}
        {!ready && !failed && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-brand-dark">
            <div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-[color:var(--sprout)] animate-spin" />
            <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.3em] text-white/60">
              Growing your harvest…
            </p>
          </div>
        )}

        {/* Floating glass trust chips passing over the video */}
        <TrustChip style={{ x: chipLeftX, y: chipY }} className="top-[24%] left-[7%]">
          Same-day harvest
        </TrustChip>
        <TrustChip style={{ x: chipRightX, y: chipY }} className="top-[30%] right-[7%]">
          Up to 40x the nutrients
        </TrustChip>
        <TrustChip style={{ x: chipRightX, y: chipY }} className="bottom-[16%] right-[12%]">
          Grown in your pincode
        </TrustChip>

        {/* Content tile — translucent green glass */}
        <div className="relative z-10 h-full flex items-center px-5 md:px-10">
          <motion.div
            style={{ y: isMobile ? 0 : contentY, opacity: isMobile ? 1 : contentOpacity }}
            className="max-w-[1400px] mx-auto w-full"
          >
            <div className="max-w-3xl rounded-[2rem] md:rounded-[2.5rem] bg-brand-forest/25 backdrop-blur-2xl border border-white/20 ring-1 ring-white/10 shadow-2xl shadow-black/30 p-8 md:p-12 lg:p-14">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="font-mono text-xs uppercase tracking-[0.28em] mb-6"
                style={{ color: ACCENT }}
              >
                Hyperlocal microgreens · Kerala
              </motion.p>

              <h1 className="font-serif font-light tracking-tighter leading-[0.9] text-[2.75rem] sm:text-5xl md:text-6xl lg:text-[4.75rem] text-white">
                <MaskedLines
                  lines={["Fresh Microgreens,", "Grown in Your", "Neighbourhood."]}
                  lineClassName="block"
                  delay={0.3}
                />
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.9 }}
                className="mt-7 max-w-xl text-base md:text-lg text-white/85 leading-relaxed"
              >
                40X Leaves grows nutrient-dense microgreens in hyperlocal farm units across Kerala —
                harvested fresh and delivered to your home through a simple WhatsApp subscription.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3, duration: 0.9 }}
                className="mt-9 flex flex-col sm:flex-row gap-4"
              >
                <a
                  href={waLink("Hi 40X Leaves, I'd like to subscribe. My pincode is ")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-whatsapp-cta"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-sprout text-white px-7 py-4 text-sm font-semibold transition-transform duration-300 hover:scale-105"
                >
                  Subscribe on WhatsApp
                  <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <Link
                  to="/franchise"
                  data-testid="hero-franchise-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white px-7 py-4 text-sm font-semibold transition-colors duration-300 hover:bg-white hover:text-brand-forest"
                >
                  Become a Franchise Partner
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Scroll hint */}
        <motion.div
          style={{ opacity: hintOpacity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/80"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll to harvest</span>
          <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
            <ChevronDown size={20} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
